import React from 'react';
import { ListItem, Popover, styled, Typography } from '@mui/material';
import CircleIcon from '@mui/icons-material/Circle';
import FormatColorResetOutlinedIcon from '@mui/icons-material/FormatColorResetOutlined';
import Flex from 'ui/Flex/Flex';
import { COLOR_SCALES, PaletteName } from 'styles/theme';
import { cloneDeep } from 'lodash';
import { useLegendDimensionContext } from 'routes/Explore/components/Contexts/LegendDimensionContext/LegendDimensionContext';
import { sortColors } from './helpers/getColors';

const StyPopoverCircleIcon = styled(CircleIcon, {
    name: 'StyPopoverCircleIcon',
    shouldForwardProp: (prop) => prop !== 'value' && prop !== 'selected',
})<{ value: string; selected: boolean }>(({ theme, value, selected }) => ({
    cursor: 'pointer',
    margin: 1,
    transform: 'scale(1)',
    transition: '100ms transform ease',
    color: value,
    ...(selected && { stroke: theme.palette.darkGrey }),
    strokeWidth: '2px',
    '&:hover': {
        transform: 'scale(1.2)',
    },
}));

const FormatColorResetStyled = styled(FormatColorResetOutlinedIcon, {
    name: 'FormatColorResetStyled',
    shouldForwardProp: (prop) => prop !== 'withHover',
})<{ withHover?: boolean }>(({ theme, withHover }) => ({
    color: theme.palette.darkGrey,
    ...(withHover && {
        transform: 'scale(1)',
        transition: '100ms transform ease',
        '&:hover': {
            transform: 'scale(1.2)',
        },
    }),
}));

const ColorMapperPicker = ({
    color,
    dimensionValue,
    defaultPaletteName,
    onChange,
}: {
    color: string | undefined;
    dimensionValue: string;
    defaultPaletteName: PaletteName;
    onChange: (dimension: string, color: string | undefined) => void;
}) => {
    // NOTE / SMELL : There should be 0 reason why a cloneDeep is needed, but
    // if we do not use the cloneDeep, the order of the color change, which change all the chart colors ...
    // It does list the color in the same order than the popover, which is not what we display in the
    // "color summary" and actually use on the chart ...
    const defaultColors = cloneDeep<string[]>(COLOR_SCALES[defaultPaletteName]);
    const {
        legendDimension: { translations },
    } = useLegendDimensionContext();
    const [anchorEl, setAnchorEl] = React.useState<Element | null>(null);
    const translatedDimensionValue =
        translations?.[dimensionValue] ?? dimensionValue;

    return (
        <>
            <ListItem>
                {color !== undefined ? (
                    <CircleIcon
                        sx={{ color }}
                        onClick={(event) => setAnchorEl(event.currentTarget)}
                    />
                ) : (
                    <FormatColorResetStyled
                        sx={{ cursor: 'pointer' }}
                        onClick={(event) => setAnchorEl(event.currentTarget)}
                    />
                )}
                <Typography sx={{ pl: 1 }}>
                    {translatedDimensionValue}
                </Typography>
            </ListItem>
            <Popover
                open={Boolean(anchorEl)}
                anchorOrigin={{
                    vertical: 'center',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'center',
                    horizontal: 'right',
                }}
                anchorEl={anchorEl}
                onClose={() => setAnchorEl(null)}
            >
                <Flex
                    sx={{
                        width: 226,
                        m: 1,
                    }}
                    justify="center"
                    flexWrap="wrap"
                >
                    {defaultColors.sort(sortColors).map((col) => (
                        <StyPopoverCircleIcon
                            key={col}
                            value={col}
                            fontSize="large"
                            selected={col === color}
                            onClick={() => {
                                onChange(dimensionValue, col);
                                setAnchorEl(null);
                            }}
                        />
                    ))}
                    <FormatColorResetStyled
                        fontSize="large"
                        sx={{ cursor: 'pointer' }}
                        withHover
                        onClick={() => {
                            onChange(dimensionValue, undefined);
                            setAnchorEl(null);
                        }}
                    />
                </Flex>
            </Popover>
        </>
    );
};

export default React.memo(ColorMapperPicker);
